import { Checkbox, Flex, Row } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useGetSystemPermissions,
  useGetUserRolePermissions,
  useGetUserRoles,
  useUpdatePermissionGrid,
} from '../../../api/hooks/useUserQuery';
import TableComponent from '../../../components/common/TableComponent';
import { FormButton } from '../../../components/form/FormBasic';
import { useNotificationContext } from '../../../contexts/NotificationContext';
import { PermissionEnum, PermissionGuardEmbedTypeEnum } from '../../../enums/PermissionEnum';
import PermissionGuard from '../../../guard/permissionGuard';
import { usePermissions } from '../../../hooks/usePermissions';
import { BaseLayout } from '../../../layouts/BaseLayout';
import { UserPermission, UserPermissionGridSaveRequest, UserRole } from '../../../types/user';
import i18n from '../../../locale/i18n';
import { RoleName } from '../../../enums/RoleNameEnm';
import { useIsOnlineContext } from '../../../contexts/IsOnlineContext';

type UserPermissionTableRow = {
  id: number;
  key: string;
  label: string;
  allowRoles?: any[];
  dependency?: string[];
};

const AccessManagementPage = () => {
  const { t } = useTranslation(['accessManagement']);
  const [userRoles, setUserRoles] = useState<UserRole[]>();
  const userRolesMutation = useGetUserRoles();
  const userPermissionGridUpdateMutation = useUpdatePermissionGrid();
  const getUserRolePermissionMutatation = useGetUserRolePermissions();
  const getSystemPermissions = useGetSystemPermissions();
  const { successNotification, errorNotification } = useNotificationContext();
  const [tableColumns, setTableColumns] = useState<any[]>();
  const [permissionTableData, setPermissionTableData] = useState<UserPermissionTableRow[]>([]);
  const { hasPermission } = usePermissions();
  const [loading, setLoading] = useState(false);
  const { isOnline } = useIsOnlineContext();
  const reverseDependency: any = {};

  const loadInitialData = async () => {
    setLoading(true);
    const {
      response: {
        data: {
          data: { data: userRolesList },
        },
      },
    } = await userRolesMutation.mutateAsync({
      allRecords: true,
      page: 1,
      size: 100,
      sort: {
        key: 'id',
        order: 'ASC',
      },
    });

    const {
      response: {
        data: { data: userPermissionsMap },
      },
    } = await getSystemPermissions.mutateAsync({
      allRecords: true,
      page: 1,
      size: 100,
      sort: {
        key: 'id',
        order: 'ASC',
      },
    });

    const {
      response: {
        data: { data: userRolePermissions },
      },
    } = await getUserRolePermissionMutatation.mutateAsync({
      allRecords: true,
      page: 1,
      size: 100,
      sort: {
        key: 'id',
        order: 'ASC',
      },
    });

    const userPermissions: UserPermission[] = [];
    Object.keys(userPermissionsMap.lowLevelPermissions).forEach((permission: any) => {
      userPermissionsMap.lowLevelPermissions[permission].forEach((element: any) => {
        const currentPermission = userRolePermissions.find(
          (rolePer: any) => rolePer.id == element.id
        );
        const allowedPermission: any[] = [];
        if (currentPermission?.roles) {
          Object.keys(currentPermission.roles).map((roleKey: string) => {
            if (currentPermission.roles[roleKey]) {
              allowedPermission.push(Number(roleKey));
            }
          });
        }

        if (element.dependency) {
          element.dependency.forEach((dependency: string) => {
            if (!reverseDependency[dependency]) {
              reverseDependency[dependency] = [];
            }
            if (!reverseDependency[dependency].includes(element.key)) {
              reverseDependency[dependency].push(element.key);
            }
          });
        }

        userPermissions.push({
          id: element.id,
          key: element.key,
          label: element.name,
          description: element.description,
          allowRoles: allowedPermission,
          dependency: element.dependency,
        });
      });
    });
    setUserRoles(userRolesList);

    const tableColumns = userRolesList.map((roleData: UserRole) => {
      return {
        title: <p className="text-center">{t(`${roleData.name}`)}</p>,
        width: 150,
        dataIndex: roleData.name,
        key: roleData.id,
        render: (_: any, record: UserPermissionTableRow) => (
          <Flex justify="center">
            <Checkbox
              id={`${record.id}__${roleData.id}`}
              checked={isRoleAllow(record, roleData)}
              name={`${record.id}__${roleData.id}`}
              disabled={
                roleData.name === RoleName.ADMIN ||
                !hasPermission([
                  PermissionEnum.MANAGE_USER_PERMISSION,
                  PermissionEnum.USER_PERMISSION,
                ])
              }
              onChange={() => {
                changeAccessManagement(record, roleData);
              }}
            ></Checkbox>
          </Flex>
        ),
      };
    });

    setTableColumns([
      {
        title: t('Permission'),
        width: 180,
        dataIndex: 'permissionName',
        key: 'permissionName',
        fixed: 'left',
        render: (_: any, record: UserPermissionTableRow) => {
          return record.label;
        },
      },
      ...tableColumns,
    ]);

    const tableRows: UserPermissionTableRow[] = userPermissions.map(
      (permission: UserPermission) => {
        return {
          id: permission.id,
          key: permission.key,
          label: t(`${permission.label}`),
          allowRoles: permission.allowRoles,
          dependency: permission.dependency,
        };
      }
    );
    setPermissionTableData(tableRows);
    setLoading(false);
  };

  useEffect(() => {
    loadInitialData();
  }, [i18n.language]);

  const changeAccessManagement = (permission: UserPermissionTableRow, role: UserRole) => {
    setPermissionTableData((prev: UserPermissionTableRow[]) => {
      const isRemoving = permission.allowRoles?.includes(role.id);

      return prev.map((item) => {
        if (item.id === permission.id) {
          let validRoles = [...(item.allowRoles || [])];

          if (!validRoles.includes(role.id)) {
            validRoles.push(role.id);
          } else {
            const reverseDeps = reverseDependency[item.key] || [];

            const firstConflict = reverseDeps
              .map((depKey: string) => prev.find((p) => p.key === depKey))
              .find((depPermission: { allowRoles: number[] }) =>
                depPermission?.allowRoles?.includes(role.id)
              );

            if (firstConflict) {
              errorNotification(
                t('cannotRemovePermissionBecauseOf', {
                  blockedPermission: permission.label,
                  reasonPermission: firstConflict.label,
                })
              );
              return item;
            }

            validRoles = validRoles.filter((id) => id !== role.id);
          }

          return { ...item, allowRoles: validRoles };
        }
        if (
          !isRemoving &&
          permission.dependency?.includes(item.key) &&
          !item.allowRoles?.includes(role.id)
        ) {
          return { ...item, allowRoles: [...(item.allowRoles || []), role.id] };
        }
        return item;
      });
    });
  };

  const onSubmitPermission = () => {
    const permissionGrid: UserPermissionGridSaveRequest[] = permissionTableData.map(
      (permission) => {
        const roleMap: Record<string, boolean> = {};
        userRoles?.forEach((role) => {
          roleMap[role.id.toString()] = permission.allowRoles?.includes(role.id) || false;
        });
        return { id: permission.id, roles: roleMap };
      }
    );

    userPermissionGridUpdateMutation.mutate(permissionGrid, {
      onSuccess: () => {
        successNotification(t('accessManagementUpdate'));
      },
    });
  };

  const isRoleAllow = (permission: any, role: UserRole) => {
    return permission.allowRoles.includes(role.id);
  };

  const content = (
    <>
      <TableComponent
        tableData={permissionTableData}
        columns={tableColumns}
        pageSize={0}
        currentPage={0}
        totalItems={0}
        scroll={{ x: 'max-content' }}
        pagination={false}
        onPaginationChanged={undefined}
        showHeader
        loading={isOnline ? loading : false}
      ></TableComponent>

      <Row className="mt-5 justify-end">
        <PermissionGuard
          permission={[PermissionEnum.MANAGE_USER_PERMISSION, PermissionEnum.USER_PERMISSION]}
          embedType={PermissionGuardEmbedTypeEnum.COMPONENT}
        >
          <FormButton
            label={t('submit')}
            type={'button'}
            isLoading={userPermissionGridUpdateMutation.isPending}
            onClick={onSubmitPermission}
          ></FormButton>
        </PermissionGuard>
      </Row>
    </>
  );

  return (
    <>
      <BaseLayout pageTitle={t('accessManagement')} content={content}></BaseLayout>
    </>
  );
};

export default AccessManagementPage;
